import React, { useMemo } from 'react'
import clsx from 'clsx'
import { Tooltip } from '../../ui/tooltip'
import { SocialReaction, SOCIAL_REACTION_EMOJIS } from '../../../types/social'
import {
  getReactionLabel,
  normalizeReactionEmoji,
  ReactionEmojiIcon,
} from './ReactionEmoji'

export interface ReactionBarProps {
  reactions: SocialReaction[]
  currentUserId?: string
  onReact: (emoji: string) => void | Promise<void>
  disabled?: boolean
  variant?: 'inline' | 'actions'
}

function reactionUserLabel(reaction: SocialReaction): string {
  return reaction.user?.name?.trim() || 'Usuário'
}

export function ReactionSummary({
  reactions,
  iconSize = 'sm',
}: {
  reactions: SocialReaction[]
  iconSize?: 'sm' | 'md' | 'lg'
}) {
  const { total, topEmojis } = useMemo(() => {
    const byEmoji = new Map<string, number>()
    for (const r of reactions) {
      const key = normalizeReactionEmoji(r.emoji)
      byEmoji.set(key, (byEmoji.get(key) || 0) + 1)
    }
    const sorted = [...byEmoji.entries()].sort((a, b) => b[1] - a[1])
    return {
      total: reactions.length,
      topEmojis: sorted.slice(0, 3).map(([emoji]) => emoji),
    }
  }, [reactions])

  const reactionsByEmoji = useMemo(() => {
    const map = new Map<string, SocialReaction[]>()
    for (const reaction of reactions) {
      const key = normalizeReactionEmoji(reaction.emoji)
      const list = map.get(key) || []
      list.push(reaction)
      map.set(key, list)
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length)
  }, [reactions])

  if (total === 0) return null

  const summary = (
    <span className="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-default">
      <span className="inline-flex items-center gap-1">
        {topEmojis.map((emoji) => (
          <span
            key={emoji}
            className="inline-flex items-center justify-center rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 overflow-hidden p-0.5"
          >
            <ReactionEmojiIcon emoji={emoji} size={iconSize} />
          </span>
        ))}
      </span>
      <span>{total} {total === 1 ? 'reação' : 'reações'}</span>
    </span>
  )

  return (
    <Tooltip
      position="top"
      delay={200}
      constrainWidth
      maxWidth={220}
      content={
        <div className="space-y-2">
          {reactionsByEmoji.map(([emoji, emojiReactions]) => (
            <div key={emoji}>
              <div className="font-medium mb-1 flex items-center gap-1.5">
                <ReactionEmojiIcon emoji={emoji} size="sm" />
                {getReactionLabel(emoji)}
              </div>
              <ul className="space-y-0.5">
                {emojiReactions.map((reaction) => (
                  <li key={reaction.id}>{reactionUserLabel(reaction)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      }
    >
      {summary}
    </Tooltip>
  )
}

const ReactionBar: React.FC<ReactionBarProps> = ({
  reactions,
  currentUserId,
  onReact,
  disabled = false,
  variant = 'inline',
}) => {
  const reactionsByEmoji = useMemo(() => {
    const map = new Map<string, SocialReaction[]>()
    for (const emoji of SOCIAL_REACTION_EMOJIS) {
      map.set(emoji, [])
    }
    for (const reaction of reactions) {
      const key = normalizeReactionEmoji(reaction.emoji)
      const list = map.get(key) || []
      list.push(reaction)
      map.set(key, list)
    }
    return map
  }, [reactions])

  const userReaction = useMemo(() => {
    const emoji = reactions.find((r) => r.user_id === currentUserId)?.emoji ?? null
    return emoji ? normalizeReactionEmoji(emoji) : null
  }, [reactions, currentUserId])

  const isActions = variant === 'actions'

  return (
    <div
      className={clsx(
        'flex flex-wrap items-center',
        isActions ? 'justify-center gap-1.5 py-1.5' : 'gap-1'
      )}
    >
      {SOCIAL_REACTION_EMOJIS.map((emoji) => {
        const emojiReactions = reactionsByEmoji.get(emoji) || []
        const count = emojiReactions.length
        const isActive = userReaction === emoji

        const button = (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onReact(emoji)}
            className={clsx(
              'inline-flex items-center gap-1 transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              isActions
                ? clsx(
                    'rounded-xl px-2.5 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700/60',
                    isActive && 'bg-primary-50 dark:bg-primary-900/30 ring-1 ring-primary-200 dark:ring-primary-700'
                  )
                : clsx(
                    'rounded-full border px-2 py-1 text-sm',
                    isActive
                      ? 'border-primary-400 bg-primary-100 dark:bg-primary-900/40 dark:border-primary-500'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  )
            )}
            title={getReactionLabel(emoji)}
          >
            <ReactionEmojiIcon emoji={emoji} size={isActions ? 'lg' : 'md'} />
            {!isActions && count > 0 && (
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 tabular-nums">
                {count}
              </span>
            )}
          </button>
        )

        if (count === 0) {
          return <React.Fragment key={emoji}>{button}</React.Fragment>
        }

        return (
          <Tooltip
            key={emoji}
            position="top"
            delay={200}
            constrainWidth
            maxWidth={220}
            content={
              <div>
                <div className="font-medium mb-1 flex items-center gap-1.5">
                  <ReactionEmojiIcon emoji={emoji} size="sm" />
                  {getReactionLabel(emoji)}
                </div>
                <ul className="space-y-0.5">
                  {emojiReactions.map((reaction) => (
                    <li key={reaction.id}>{reactionUserLabel(reaction)}</li>
                  ))}
                </ul>
              </div>
            }
          >
            {button}
          </Tooltip>
        )
      })}
    </div>
  )
}

export default ReactionBar
